import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip as ChartTooltip, type ChartData, type ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Card, ErrorState, Skeleton } from '@liftango/ui-elements';
import { opsColors, opsFontFamily, opsFontSize, opsFontWeight, opsSpace } from '../../../../../theme/opsTokens';
import { onBoardMinutes, peakLoadByHour, quantile, requestBuckets, waitMinutes, type HourlyPeakLoad } from '../../../../../services/simulation/runReport';
import type { ReportSource, RunReport } from '../useRunReport';
import { formatClock, formatNumber } from './reportFormat';
import { busiestHour, fullestHour, histogram, hourLabel, hourSpan, onHours, wholeHourDay, type Histogram } from './chartData';
import { SourceProblem } from './SourceProblem';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

const CHART_PLUGINS = [ChartDataLabels];
const WIDE_CHART_HEIGHT = '160px';
const CHART_HEIGHT = '140px';
const HISTOGRAM_BINS = 12;
const HOUR_BUCKET_MINUTES = 60;
const MEDIAN = 0.5;
const P90 = 0.9;
const PERCENT = 100;
const VALUE_LABEL_FONT_PX = 10;
const VALUE_LABEL_OFFSET_PX = -2;
const VALUE_LABEL_HEADROOM_PX = 14;
const BAR_RADIUS_PX = 2;
const CHART_SKELETON = { width: '100%', height: CHART_HEIGHT } as const;
const COMPARE_COLOUR = opsColors.neutral.inkFaint;
const COMPARE_UNMATCHED_COLOUR = alpha(opsColors.semantic.error.color, 0.35);
const RUN_STACK = 'run';
const COMPARE_STACK = 'compare';
const LEGEND_SWATCH = { width: '10px', height: '10px', borderRadius: '2px' } as const;

export interface Series {
  readonly label: string;
  /** Null draws no bar, for an hour with no data. */
  readonly values: readonly (number | null)[];
  readonly colour: string;
  /** The comparison run: drawn muted and without value labels. */
  readonly compare?: boolean;
  /** Series with the same stack id stack on one bar, when the chart is stacked. */
  readonly stack?: string;
}

/** The total of this run's series at one bar. */
export function totalAt(series: readonly Series[], index: number): number {
  return series.reduce((sum, item) => (item.compare ? sum : sum + (item.values[index] ?? 0)), 0);
}

/** A stacked bar carries its total on the top series only. */
export function chartOptions(series: readonly Series[], stacked: boolean, valueLabel: (value: number) => string): ChartOptions<'bar'> {
  const topOfStack = series.reduce((top, item, index) => (item.compare ? top : index), -1);
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: VALUE_LABEL_OFFSET_PX,
        color: opsColors.neutral.inkSoft,
        font: { family: opsFontFamily.mono, size: VALUE_LABEL_FONT_PX },
        display: (context) => {
          if (series[context.datasetIndex]?.compare) return false;
          return stacked
            ? context.datasetIndex === topOfStack && totalAt(series, context.dataIndex) > 0
            : Number(context.dataset.data[context.dataIndex] ?? 0) > 0;
        },
        formatter: (value: number, context) => valueLabel(stacked ? totalAt(series, context.dataIndex) : value),
      },
    },
    layout: { padding: { top: VALUE_LABEL_HEADROOM_PX } },
    scales: {
      x: { stacked, grid: { display: false }, ticks: { font: { family: opsFontFamily.mono, size: VALUE_LABEL_FONT_PX }, color: opsColors.neutral.inkMute } },
      y: { stacked, display: false, beginAtZero: true },
    },
  };
}

interface ChartCardProps {
  readonly id: string;
  readonly title: string;
  readonly summary: string | null;
  readonly axis: string;
  readonly height: string;
  readonly labels: readonly string[];
  readonly series: readonly Series[];
  readonly stacked?: boolean;
  readonly valueLabel: (value: number) => string;
}

function ChartCard({ id, title, summary, axis, height, labels, series, stacked = false, valueLabel }: ChartCardProps) {
  const data = useMemo<ChartData<'bar', (number | null)[]>>(
    () => ({
      labels: [...labels],
      datasets: series.map((item) => ({ label: item.label, data: [...item.values], backgroundColor: item.colour, borderRadius: BAR_RADIUS_PX, stack: item.stack })),
    }),
    [labels, series]
  );
  const options = useMemo(() => chartOptions(series, stacked, valueLabel), [series, stacked, valueLabel]);

  return (
    <Card data-testid={`sim-results.report.charts.${id}`}>
      <ChartHeading title={title} summary={summary} />
      <Box role="img" aria-label={summary ? `${title}. ${summary}` : title} sx={{ height }}>
        <Bar data={data} options={options} plugins={CHART_PLUGINS} />
      </Box>
      <Box sx={{ mt: opsSpace[1], fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute }}>{axis}</Box>
    </Card>
  );
}

function ChartHeading({ title, summary }: { title: string; summary: string | null }) {
  return (
    <Box sx={{ mb: opsSpace[2] }}>
      <Box component="h3" sx={{ m: 0, fontSize: opsFontSize.body, fontWeight: opsFontWeight.semibold, color: opsColors.neutral.ink }}>
        {title}
      </Box>
      {summary ? <Box sx={{ fontSize: opsFontSize.xs, color: opsColors.neutral.inkSoft }}>{summary}</Box> : null}
    </Box>
  );
}

function PendingCard({ id, title, report, sources }: { id: string; title: string; report: RunReport; sources: readonly ReportSource[] }) {
  return (
    <Card data-testid={`sim-results.report.charts.${id}`}>
      <ChartHeading title={title} summary={null} />
      {sources.some((source) => report[source].status === 'error') ? (
        <SourceProblem report={report} sources={sources} />
      ) : (
        <Skeleton {...CHART_SKELETON} />
      )}
    </Card>
  );
}

export function binLabels({ bins, edgeDigits }: Histogram, locale: string): string[] {
  return bins.map((bin) => `${formatNumber(bin.start, locale, edgeDigits)}–${formatNumber(bin.end, locale, edgeDigits)}`);
}

const finite = (values: readonly (number | null)[]): number[] => values.filter((value): value is number => value != null && Number.isFinite(value));

const COMPARE_SOURCES: readonly ReportSource[] = ['compareActivities', 'compareVehicles', 'compareUnmatched'];

export interface ReportChartsProps {
  readonly report: RunReport;
  /** The comparison run's name, when one is selected. */
  readonly compareLabel: string | null;
}

export const ReportCharts = memo(function ReportCharts({ report, compareLabel }: ReportChartsProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const derived = report.derived;
  const compareFailed = COMPARE_SOURCES.filter((source) => report[source].status === 'error');
  // A comparison with any source failed is left off every chart, so no chart shows half of it.
  const compare = compareFailed.length === 0 ? report.compareDerived : null;
  const unmatched = report.unmatched.status === 'ready' ? report.unmatched.data : null;
  const compareUnmatched = report.compareUnmatched.status === 'ready' ? report.compareUnmatched.data : null;
  const hourDay = useMemo(() => (derived?.day ? wholeHourDay(derived.day) : null), [derived]);
  const compareHourDay = useMemo(() => (compare?.day ? wholeHourDay(compare.day) : null), [compare]);

  const distributions = useMemo(() => {
    if (!derived || !hourDay) return null;
    const waits = finite(derived.rides.map(waitMinutes));
    const onBoard = finite(derived.rides.map(onBoardMinutes));
    const compareWaits = compare ? finite(compare.rides.map(waitMinutes)) : null;
    const compareOnBoard = compare ? finite(compare.rides.map(onBoardMinutes)) : null;
    const waitSpan = [...waits, ...(compareWaits ?? [])];
    const onBoardSpan = [...onBoard, ...(compareOnBoard ?? [])];
    const main = { buckets: peakLoadByHour(derived.shifts, hourDay), dayStartMs: hourDay.startMs };
    const other = compare && compareHourDay ? { buckets: peakLoadByHour(compare.shifts, compareHourDay), dayStartMs: compareHourDay.startMs } : null;
    const hours = hourSpan(other ? [main, other] : [main]);
    const loadOf = (hour: HourlyPeakLoad) => (hour.standard == null ? null : hour.standard * PERCENT);
    return {
      waits,
      onBoard,
      waitBins: histogram(waits, HISTOGRAM_BINS, waitSpan),
      onBoardBins: histogram(onBoard, HISTOGRAM_BINS, onBoardSpan),
      compareWaitBins: compareWaits ? histogram(compareWaits, HISTOGRAM_BINS, waitSpan) : null,
      compareOnBoardBins: compareOnBoard ? histogram(compareOnBoard, HISTOGRAM_BINS, onBoardSpan) : null,
      fullest: fullestHour(main.buckets),
      hours,
      load: onHours(hours, main, loadOf),
      compareLoad: other ? onHours(hours, other, loadOf) : null,
    };
  }, [derived, hourDay, compare, compareHourDay]);

  const requests = useMemo(() => {
    if (!derived || !hourDay || !unmatched) return null;
    const main = { buckets: requestBuckets(derived.rides, unmatched, hourDay, HOUR_BUCKET_MINUTES), dayStartMs: hourDay.startMs };
    const other =
      compare && compareHourDay && compareUnmatched
        ? { buckets: requestBuckets(compare.rides, compareUnmatched, compareHourDay, HOUR_BUCKET_MINUTES), dayStartMs: compareHourDay.startMs }
        : null;
    const hours = hourSpan(other ? [main, other] : [main]);
    return {
      busiest: busiestHour(main.buckets),
      hours,
      matched: onHours(hours, main, (bucket) => bucket.matched),
      unmatched: onHours(hours, main, (bucket) => bucket.unmatched),
      compareMatched: other ? onHours(hours, other, (bucket) => bucket.matched) : null,
      compareUnmatched: other ? onHours(hours, other, (bucket) => bucket.unmatched) : null,
    };
  }, [derived, hourDay, unmatched, compare, compareHourDay, compareUnmatched]);

  const distances = useMemo(() => {
    if (!derived || !report.distances) return null;
    const { kmByRide } = report.distances;
    return finite(derived.rides.map((ride) => kmByRide.get(ride.rideId) ?? (ride.externalReferenceId ? kmByRide.get(ride.externalReferenceId) : undefined) ?? null));
  }, [derived, report.distances]);

  const count = (value: number) => formatNumber(value, locale);
  const percent = (value: number) => t('results.report.units.percentValue', { value: formatNumber(value, locale) });
  const minutes = (value: number | null) => (value == null ? t('results.report.noValue') : formatNumber(value, locale, 1));
  const title = (key: string) => t(`results.report.charts.${key}.title`);
  const compareName = compareLabel ?? t('results.report.charts.legend.comparison');
  const compareSeries = (values: readonly (number | null)[] | null, stack?: string, colour = COMPARE_COLOUR, label = compareName): Series[] =>
    values ? [{ label, values, colour, compare: true, stack }] : [];
  const binCounts = (bins: Histogram | null) => (bins ? bins.bins.map((bin) => bin.count) : null);

  const busiest = requests?.busiest ?? null;
  const fullest = distributions?.fullest ?? null;
  const distanceBins = distances ? histogram(distances, HISTOGRAM_BINS) : null;
  const showsComparison = compare != null;

  return (
    <Box data-testid="sim-results.report.charts" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: opsSpace[5] }}>
      {compareLabel != null ? (
        <Box sx={{ gridColumn: '1 / -1' }} data-testid="sim-results.report.charts.comparison">
          {compareFailed.length > 0 ? (
            <ErrorState
              title={t('results.report.errors.compareRides')}
              onRetry={() => compareFailed.forEach((source) => report.retry(source))}
              retryLabel={t('results.report.retry')}
            />
          ) : showsComparison ? (
            <ChartsLegend thisRun={t('results.report.charts.legend.thisRun')} comparison={compareName} label={t('results.report.charts.legend.label')} />
          ) : (
            <Box sx={{ fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute }}>{t('results.report.charts.comparisonLoading')}</Box>
          )}
        </Box>
      ) : null}
      <Box sx={{ gridColumn: '1 / -1' }}>
        {requests ? (
          <ChartCard
            id="requests"
            title={title('requests')}
            summary={busiest ? t('results.report.charts.requests.summary', { hour: formatClock(busiest.startMs, locale), count: busiest.matched + busiest.unmatched }) : null}
            axis={t('results.report.charts.hourAxis')}
            height={WIDE_CHART_HEIGHT}
            labels={requests.hours.map(hourLabel)}
            stacked
            series={[
              { label: t('results.report.charts.requests.matched'), values: requests.matched, colour: opsColors.semantic.info.color, stack: RUN_STACK },
              { label: t('results.report.charts.requests.unmatched'), values: requests.unmatched, colour: opsColors.semantic.error.color, stack: RUN_STACK },
              ...compareSeries(requests.compareMatched, COMPARE_STACK),
              ...compareSeries(requests.compareUnmatched, COMPARE_STACK, COMPARE_UNMATCHED_COLOUR, `${compareName} · ${t('results.report.charts.requests.unmatched')}`),
            ]}
            valueLabel={count}
          />
        ) : (
          <PendingCard id="requests" title={title('requests')} report={report} sources={['activities', 'vehicles', 'unmatched']} />
        )}
      </Box>
      {distributions ? (
        <>
          <ChartCard
            id="wait"
            title={title('wait')}
            summary={t('results.report.charts.wait.summary', { median: minutes(quantile(distributions.waits, MEDIAN)), p90: minutes(quantile(distributions.waits, P90)) })}
            axis={t('results.report.charts.minutesAxis')}
            height={CHART_HEIGHT}
            labels={binLabels(distributions.waitBins, locale)}
            series={[
              { label: title('wait'), values: distributions.waitBins.bins.map((bin) => bin.count), colour: opsColors.semantic.info.color },
              ...compareSeries(binCounts(distributions.compareWaitBins)),
            ]}
            valueLabel={count}
          />
          <ChartCard
            id="onBoard"
            title={title('onBoard')}
            summary={t('results.report.charts.onBoard.summary', { median: minutes(quantile(distributions.onBoard, MEDIAN)), p90: minutes(quantile(distributions.onBoard, P90)) })}
            axis={t('results.report.charts.minutesAxis')}
            height={CHART_HEIGHT}
            labels={binLabels(distributions.onBoardBins, locale)}
            series={[
              { label: title('onBoard'), values: distributions.onBoardBins.bins.map((bin) => bin.count), colour: opsColors.semantic.info.color },
              ...compareSeries(binCounts(distributions.compareOnBoardBins)),
            ]}
            valueLabel={count}
          />
        </>
      ) : (
        <>
          <PendingCard id="wait" title={title('wait')} report={report} sources={['activities', 'vehicles']} />
          <PendingCard id="onBoard" title={title('onBoard')} report={report} sources={['activities', 'vehicles']} />
        </>
      )}
      {distances && distanceBins ? (
        <ChartCard
          id="distance"
          title={title('distance')}
          summary={distances.length > 0 ? t('results.report.charts.distance.summary', { median: minutes(quantile(distances, MEDIAN)) }) : null}
          axis={showsComparison ? t('results.report.charts.thisRunOnly', { axis: t('results.report.charts.kmAxis') }) : t('results.report.charts.kmAxis')}
          height={CHART_HEIGHT}
          labels={binLabels(distanceBins, locale)}
          series={[{ label: title('distance'), values: distanceBins.bins.map((bin) => bin.count), colour: opsColors.semantic.info.color }]}
          valueLabel={count}
        />
      ) : (
        <PendingCard id="distance" title={title('distance')} report={report} sources={['activities', 'vehicles', 'paths']} />
      )}
      {distributions ? (
        <ChartCard
          id="peakLoad"
          title={title('peakLoad')}
          summary={fullest?.standard != null ? t('results.report.charts.peakLoad.summary', { hour: formatClock(fullest.startMs, locale), value: percent(fullest.standard * PERCENT) }) : null}
          axis={t('results.report.charts.hourAxis')}
          height={CHART_HEIGHT}
          labels={distributions.hours.map(hourLabel)}
          series={[{ label: title('peakLoad'), values: distributions.load, colour: opsColors.semantic.info.color }, ...compareSeries(distributions.compareLoad)]}
          valueLabel={percent}
        />
      ) : (
        <PendingCard id="peakLoad" title={title('peakLoad')} report={report} sources={['activities', 'vehicles']} />
      )}
    </Box>
  );
});

function ChartsLegend({ thisRun, comparison, label }: { thisRun: string; comparison: string; label: string }) {
  const item = (text: string, colour: string) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: opsSpace[1] }}>
      <Box component="span" aria-hidden="true" sx={{ ...LEGEND_SWATCH, backgroundColor: colour }} />
      {text}
    </Box>
  );
  return (
    <Box role="group" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', gap: opsSpace[4], fontSize: opsFontSize.xs, color: opsColors.neutral.inkSoft }}>
      {item(thisRun, opsColors.semantic.info.color)}
      {item(comparison, COMPARE_COLOUR)}
    </Box>
  );
}
